import React, { useState, useRef } from "react";
import { Empty, Row, Col, InputRef, Flex } from "antd";
import { JobDescriptionEntity } from "../../../../../Entities/JobDescription";
import { InterviewRound } from "../../../../../Dto/GenerateJobDescription.dto";
import useOrganization from "../../../../../Hooks/useOrganization";
import { openNotification } from "../../../../../Components/Notification";
import { useSearchParams } from "react-router-dom";
import JobShareDrawer from "../../../../../Components/JobShareDrawer";
import {
  BasicDetailsSection,
  RequirementsInsightCard,
  AssessmentsInsightCard,
  InterviewersAndGuestsCard,
  NotificationsSection,
} from "./components";
import InterviewRoundOverviewModal from "../InterviewRoundOverviewModal";
import JobShareSection from "../../../../../Components/JobShareSection";
import "./index.scss";

interface InsightsDashboardProps {
  jobPost: JobDescriptionEntity | null;
  interviewRoundDetails: InterviewRound[];
}

const InsightsDashboard: React.FC<InsightsDashboardProps> = ({
  jobPost,
  interviewRoundDetails,
}) => {
  const [, setSearchParams] = useSearchParams();
  const { org } = useOrganization();
  const themeColor = org?.theme?.primaryColor || "#1890ff";

  // Drawer & Modal states
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string>("");
  const [showRoundOverviewModal, setShowRoundOverviewModal] = useState(false);
  const [selectedRound, setSelectedRound] = useState<InterviewRound | null>(
    null,
  );

  // Individual expanded states for each insight card
  const [requirementsExpanded, setRequirementsExpanded] = useState(false);
  const [assessmentsExpanded, setAssessmentsExpanded] = useState(false);
  const [interviewersExpanded, setInterviewersExpanded] = useState(false);

  // Refs
  const shareInputRef = useRef<InputRef>(null);

  // Early return for empty state
  if (!jobPost) {
    return (
      <Flex justify="center" align="center" style={{ height: "100%" }}>
        <Empty
          description="No matching job post was found."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Flex>
    );
  }

  // Job link
  const jobLink =
    org?.orgDomain && jobPost?.slug
      ? `https://${org.orgDomain}/${jobPost?.slug}/requirements`
      : "";

  // Handlers
  const navigateTo = () => {
    window.open(jobLink, "_blank");
  };

  const fallbackCopyToClipboard = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand("copy");
      openNotification(
        "success",
        "Copied",
        "The URL has been copied to your clipboard.",
      );
    } catch (err) {
      openNotification("error", "Copy failed", "Unable to copy the URL.");
    }

    document.body.removeChild(textArea);
  };

  const handleCopy = async () => {
    if (org?.orgDomain) {
      if (navigator.clipboard) {
        try {
          await navigator.clipboard.writeText(jobLink);
          openNotification(
            "success",
            "Copied",
            "The careers site URL has been copied to your clipboard.",
          );
        } catch (err) {
          fallbackCopyToClipboard(jobLink);
        }
      } else {
        fallbackCopyToClipboard(jobLink);
      }
    }
  };

  const handlePlatformClick = (platform: string) => {
    setSelectedPlatform(platform);
    setIsShareDrawerOpen(true);
  };

  const handleShareDrawerClose = () => {
    setIsShareDrawerOpen(false);
    setSelectedPlatform("");
  };

  const handleRoundCardClick = async (round: any) => {
    const roundId = round.interviewRoundId || round._id || "";

    let fullRoundDetails = interviewRoundDetails?.find(
      (r: any) => r._id === roundId || r.interviewRoundId === roundId,
    );

    if (!fullRoundDetails) {
      try {
        const interviewService = (
          await import("../../../../../Service/interview.service")
        ).default;
        const allRounds =
          await interviewService.getAllInterviewRoundsSpecificToOrganization(
            false,
          );

        fullRoundDetails = allRounds.find((r: any) => r._id === roundId);

        if (
          !fullRoundDetails &&
          (roundId === "profile-match" ||
            roundId === "job-alignment" ||
            roundId === "overall-assessment")
        ) {
          fullRoundDetails = allRounds.find((r: any) => {
            if (roundId === "profile-match") {
              const isGeneric = r.isGeneric;
              const categories = r.category || [];
              const hasProfileMatchCategory = categories.some(
                (cat: string) =>
                  cat.toLowerCase().replaceAll(/\s+/g, "") === "profilematch",
              );
              return isGeneric && hasProfileMatchCategory;
            } else if (roundId === "job-alignment") {
              const isGeneric = r.isGeneric;
              const categories = r.category || [];
              const hasJobAlignmentCategory = categories.some(
                (cat: string) =>
                  cat.toLowerCase().replaceAll(/\s+/g, "") === "jobalignment",
              );
              return isGeneric && hasJobAlignmentCategory;
            } else if (roundId === "overall-assessment") {
              const isGeneric = r.isGeneric;
              const titleMatch =
                r.title?.toLowerCase().trim() === "overall assessment";
              return isGeneric && titleMatch;
            }
            return false;
          });
        }
      } catch (error) {
        console.error("Error fetching round details:", error);
      }
    }

    if (!fullRoundDetails) {
      const roundTitle =
        round.title ||
        (roundId === "profile-match"
          ? "Profile Matching"
          : roundId === "job-alignment"
            ? "Job Alignment"
            : roundId === "overall-assessment"
              ? "Overall Assessment"
              : "Untitled Round");

      fullRoundDetails = {
        _id: roundId,
        interviewRoundId: roundId,
        title: roundTitle,
        category: [],
        questionGenerationPrompt: "",
        answerType: "descriptive",
        ...round,
      };
    }

    setShowRoundOverviewModal(true);
    setSelectedRound(fullRoundDetails as InterviewRound);
  };

  const handleRoundViewModalClose = () => {
    setShowRoundOverviewModal(false);
    setSelectedRound(null);
  };

  return (
    <section className="insights-dashboard">
      <Row
        className="equal-height-row"
        style={{ marginBottom: 16 }}
        gutter={[
          { xs: 12, sm: 16, md: 16 },
          { xs: 12, sm: 16, md: 16 },
        ]}
      >
        {/* Job Info Card */}
        <Col xs={24} lg={12}>
          {/* Basic Details Section */}
          <BasicDetailsSection
            jobPost={jobPost}
            jobLink={jobLink}
            themeColor={themeColor}
          />
        </Col>
        <Col xs={24} lg={12}>
          {/* Share Section */}
          <JobShareSection
            jobLink={jobLink}
            jobTitle={jobPost?.title || "Job Opening"}
            hasActiveJobPost={jobPost?.isActive ?? false}
            inputRef={shareInputRef}
            onNavigateTo={navigateTo}
            onCopy={handleCopy}
            onPlatformClick={handlePlatformClick}
          />
        </Col>
      </Row>

      {/* Requirements & Assessments Row */}
      <Row
        className="equal-height-row"
        style={{ marginBottom: 16 }}
        gutter={[
          { xs: 12, sm: 16, md: 16 },
          { xs: 12, sm: 16, md: 16 },
        ]}
      >
        <Col xs={24} lg={12}>
          <RequirementsInsightCard
            jobPost={jobPost}
            themeColor={themeColor}
            expanded={requirementsExpanded}
            onExpandChange={setRequirementsExpanded}
            onRequirementClick={(requirementId, matchStatus) => {
              // Navigate to applications tab with requirement filter
              setSearchParams({
                tabIndex: "applications",
                requirementId: String(requirementId),
                requirementMatch: matchStatus, // matched, unmatched, or partial
              });
            }}
            onProfileMatchStatusClick={(status) => {
              // Navigate to applications tab with profile match status filter
              const params: Record<string, string> = {
                tabIndex: "applications",
              };
              if (status !== "all") {
                // Map "pending" to "NA" as expected by the filter system
                params.profileMatchStatus =
                  status === "pending" ? "NA" : status;
              }
              setSearchParams(params);
            }}
          />
        </Col>
        <Col xs={24} lg={12}>
          <AssessmentsInsightCard
            jobPost={jobPost}
            interviewRoundDetails={interviewRoundDetails}
            themeColor={themeColor}
            expanded={assessmentsExpanded}
            onExpandChange={setAssessmentsExpanded}
            onRoundClick={(roundId, status) => {
              // Navigate to applications with filter or open modal
              handleRoundCardClick({ interviewRoundId: roundId });
            }}
            onAssessmentStatusClick={(status) => {
              // Navigate to applications tab with assessment (roundStatus) filter
              const params: Record<string, string> = {
                tabIndex: "applications",
              };
              if (status !== "all") {
                params.roundStatus = status;
              }
              setSearchParams(params);
            }}
          />
        </Col>
      </Row>

      {/* Interviewers & Guests Section */}
      {jobPost.onlineInterview && (
        <Row
          gutter={[
            { xs: 12, sm: 16, md: 16 },
            { xs: 12, sm: 16, md: 16 },
          ]}
          style={{ marginBottom: 16 }}
        >
          <Col xs={24} lg={12}>
            <InterviewersAndGuestsCard
              jobPost={jobPost}
              themeColor={themeColor}
              expanded={interviewersExpanded}
              onExpandChange={setInterviewersExpanded}
              onPendingSlotClick={() => {
                // Navigate to applications tab with interview status filter for pending slot
                setSearchParams({
                  tabIndex: "applications",
                  interviewStatus: "not_scheduled",
                });
              }}
            />
          </Col>
        </Row>
      )}

      {/* Modals & Drawers */}
      {selectedRound && showRoundOverviewModal && (
        <InterviewRoundOverviewModal
          handleClose={handleRoundViewModalClose}
          round={selectedRound}
          showroundOverviewModal={showRoundOverviewModal}
          requirements={jobPost?.requirements || []}
        />
      )}

      <JobShareDrawer
        isOpen={isShareDrawerOpen}
        onClose={handleShareDrawerClose}
        jobTitle={`${jobPost?.title || "Job Post"}`}
        jobLink={jobLink}
        selectedPlatform={selectedPlatform}
      />
    </section>
  );
};

export default InsightsDashboard;
